/* One binding's two scheduling affordances, side by side.
 *
 * **Two independent cards, never a mode toggle**, and the design is emphatic about it for a reason
 * the backend shares: a binding may carry a nightly schedule *and* a harvest dependency, one, or
 * neither, and they are stored in two tables precisely because a single row with a mode enum cannot
 * express "both". A segmented control here would make the commonest useful arrangement
 * unreachable.
 *
 * The left card is time-driven and adds `strategy` and `severity_floor` to the shared recurrence.
 * The right card is event-driven and has **no time, cadence, weekdays or timezone at all** — it
 * fires from the harvest's own clock, and offering a time there would be a control that does
 * nothing.
 */

import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ExecutionSchedule, ExecutionTrigger } from '@/shared/api/generated/rules';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { SelectField } from '@/shared/ui/Field';

import {
  useConfigureExecutionSchedule,
  useConfigureExecutionTrigger,
  useExecutionSchedule,
  useExecutionTrigger,
  useSetExecutionScheduleEnabled,
  useSetExecutionTriggerEnabled,
} from '../api/scheduling';
import {
  OUTCOME_TONE,
  SCHEDULABLE_STRATEGIES,
  SEVERITY_FLOORS,
  asOutcome,
  defaultRecurrence,
  formatInstant,
  presenceOf,
  recurrenceOf,
  recurrenceProblem,
  type RecurrenceDraft,
} from '../model/presentation';
import { RecurrenceFields } from './RecurrenceFields';
import { ScheduleFacts, StateChip } from './ScheduleParts';

type Strategy = 'full' | 'sampled';
type Floor = 'blocker' | 'error' | 'warning' | 'info';

export interface BindingSchedulingProps {
  readonly bindingId: string;
  /** This tenant's connections, for the trigger's source picker and for naming the one it holds.
   *
   * Passed in rather than read here: the rule's page already lists connections for other reasons,
   * and a card fetching its own would issue one request per binding on a page showing several.
   */
  readonly connections: readonly { readonly id: string; readonly name: string }[];
  readonly durable: boolean;
}

export function BindingScheduling({
  bindingId,
  connections,
  durable,
}: BindingSchedulingProps): ReactElement {
  return (
    <div className="lq-sched__pair">
      <ExecutionScheduleCard bindingId={bindingId} durable={durable} />
      <HarvestTriggerCard
        bindingId={bindingId}
        connections={connections}
        durable={durable}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ time-driven */

function ExecutionScheduleCard({
  bindingId,
  durable,
}: {
  readonly bindingId: string;
  readonly durable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const schedule = useExecutionSchedule(bindingId);
  const configure = useConfigureExecutionSchedule();
  const setEnabled = useSetExecutionScheduleEnabled();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<RecurrenceDraft>(defaultRecurrence());
  /* **No default, and that is the design's rule made structural.** `strategy` is required by the
     API with no default because `full` and `sampled` differ by orders of magnitude in what they
     read from a customer's database — so the form starts empty and the reader must choose. A
     pre-selected `full` would make the expensive promise silently. */
  const [strategy, setStrategy] = useState<Strategy | ''>('');
  const [floor, setFloor] = useState<Floor>('info');

  const title = t('scheduling.execution.title');

  if (schedule.isError) {
    return <RefusedCard title={title} error={schedule.error} kind="execution" />;
  }
  if (!schedule.isSuccess) {
    return (
      <Card title={title}>
        <p className="lq-sched__detail">{t('scheduling.loading')}</p>
      </Card>
    );
  }

  const stored: ExecutionSchedule = schedule.data;
  const presence = presenceOf(stored);
  const outcome = asOutcome(stored.last_outcome);

  const openEditor = (): void => {
    setDraft(recurrenceOf(stored));
    setStrategy(stored.strategy ?? '');
    setFloor(stored.severity_floor ?? 'info');
    configure.reset();
    setEditing(true);
  };

  const problem =
    strategy === '' ? 'scheduling.form.invalid.noStrategy' : recurrenceProblem(draft);
  const refusal = refusalOf(configure.error, configure.isError, t);

  if (editing) {
    return (
      <Card title={title}>
        <RecurrenceFields
          value={draft}
          onChange={(next) => {
            setDraft(next);
          }}
        />
        <div className="lq-sched__form">
          <SelectField
            label={t('scheduling.form.strategy')}
            value={strategy}
            options={[
              { value: '', label: t('scheduling.form.strategy.choose') },
              /* **Two options, and `incremental` is not one of them.** It is a real
                 `ExecutionStrategy` the platform refuses to schedule, because it renders
                 identically to `full` today — so a control offering it would let a reader
                 configure a nightly run believing it reads only new rows while it scans whole
                 tables. The server answers 422; this list is what stops it being asked for. */
              ...SCHEDULABLE_STRATEGIES.map((each) => ({
                value: each,
                label: t(`scheduling.strategy.${each}`),
              })),
            ]}
            onChange={(next) => {
              setStrategy(next);
            }}
          />
          <SelectField
            label={t('scheduling.form.severityFloor')}
            value={floor}
            hint={t('scheduling.form.severityFloorHint')}
            options={SEVERITY_FLOORS.map((each) => ({
              value: each,
              label: t(`scheduling.severity.${each}`),
            }))}
            onChange={(next) => {
              setFloor(next);
            }}
          />
        </div>
        {problem !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{t(problem)}</p>
        ) : null}
        {refusal !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{refusal}</p>
        ) : null}
        <div className="lq-sched__actions">
          <Button
            tone="primary"
            disabled={problem !== null}
            busy={configure.isPending}
            onClick={() => {
              if (strategy === '') {
                return;
              }
              configure.mutate(
                {
                  bindingId,
                  body: {
                    enabled: stored.configured ? stored.enabled : false,
                    cadence: draft.cadence,
                    local_time: draft.localTime,
                    timezone: draft.timezone,
                    weekdays: [...draft.weekdays],
                    strategy,
                    severity_floor: floor,
                  },
                },
                {
                  onSuccess: () => {
                    setEditing(false);
                  },
                },
              );
            }}
          >
            {t('scheduling.form.save')}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              setEditing(false);
              configure.reset();
            }}
          >
            {t('scheduling.form.cancel')}
          </Button>
        </div>
      </Card>
    );
  }

  if (presence.kind === 'unconfigured') {
    return (
      <Card title={title}>
        <p className="lq-sched__line">
          <span className="lq-sched__lead">{t('scheduling.state.unconfigured')}</span>{' '}
          {t('scheduling.execution.unconfigured')}
        </p>
        <div className="lq-sched__actions">
          <Button tone="secondary" onClick={openEditor}>
            {t('scheduling.execution.setSchedule')}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card title={title} hint={<StateChip presence={presence} />}>
      <div className={presence.kind === 'disabled' ? 'lq-sched__dimmed' : undefined}>
        <p className="lq-sched__describes">{stored.describes ?? ''}</p>
        <ScheduleFacts
          localTime={stored.local_time}
          timezone={stored.timezone}
          cadence={stored.cadence}
          weekdays={stored.weekdays}
          nextRunAt={stored.next_run_at ?? null}
          lastRunAt={stored.last_run_at ?? null}
          outcome={outcome}
          enabled={stored.enabled}
        />
        <div className="lq-sched__facts">
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.strategy')}</div>
            <div className="lq-sched__value">
              {stored.strategy === undefined
                ? '—'
                : t(`scheduling.strategy.${stored.strategy}.long`)}
            </div>
          </div>
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.severityFloor')}</div>
            <div className="lq-sched__value">
              {stored.severity_floor === undefined
                ? '—'
                : t(`scheduling.severity.${stored.severity_floor}.long`)}
            </div>
          </div>
        </div>
        {stored.last_detail !== null && stored.last_detail !== undefined ? (
          <p className="lq-sched__detail">{stored.last_detail}</p>
        ) : null}
      </div>

      <StateNotes presence={presence} needsAttention={stored.needs_attention === true} />
      {!durable ? (
        <p className="lq-sched__inline lq-sched--warn">
          {t('scheduling.state.notDurable')}
        </p>
      ) : null}

      <ToggleRow
        enabled={stored.enabled}
        busy={setEnabled.isPending}
        error={
          setEnabled.isError
            ? setEnabled.error
            : configure.error instanceof ApiError && configure.error.status === 403
              ? configure.error
              : null
        }
        onEdit={openEditor}
        onToggle={() => {
          setEnabled.mutate({ id: bindingId, enabled: !stored.enabled });
        }}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ event-driven */

function HarvestTriggerCard({
  bindingId,
  connections,
  durable,
}: {
  readonly bindingId: string;
  readonly connections: readonly { readonly id: string; readonly name: string }[];
  readonly durable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const trigger = useExecutionTrigger(bindingId);
  const configure = useConfigureExecutionTrigger();
  const setEnabled = useSetExecutionTriggerEnabled();
  const [editing, setEditing] = useState(false);
  const [connectionId, setConnectionId] = useState('');
  const [strategy, setStrategy] = useState<Strategy | ''>('');
  const [floor, setFloor] = useState<Floor>('info');

  const title = t('scheduling.trigger.title');

  if (trigger.isError) {
    return <RefusedCard title={title} error={trigger.error} kind="trigger" />;
  }
  if (!trigger.isSuccess) {
    return (
      <Card title={title}>
        <p className="lq-sched__detail">{t('scheduling.loading')}</p>
      </Card>
    );
  }

  const stored: ExecutionTrigger = trigger.data;
  const presence = presenceOf(stored);
  const outcome = asOutcome(stored.last_outcome);
  const sourceName =
    connections.find((each) => each.id === stored.connection_id)?.name ?? null;

  const openEditor = (): void => {
    setConnectionId(stored.connection_id ?? '');
    setStrategy(stored.strategy ?? '');
    setFloor(stored.severity_floor ?? 'info');
    configure.reset();
    setEditing(true);
  };

  const problem =
    connectionId === ''
      ? 'scheduling.form.invalid.noConnection'
      : strategy === ''
        ? 'scheduling.form.invalid.noStrategy'
        : null;
  const refusal = refusalOf(configure.error, configure.isError, t);

  if (editing) {
    return (
      <Card title={title}>
        <div className="lq-sched__form">
          <SelectField
            label={t('scheduling.form.connection')}
            value={connectionId}
            hint={t('scheduling.form.connectionHint')}
            options={[
              { value: '', label: t('scheduling.form.connection.choose') },
              ...connections.map((each) => ({ value: each.id, label: each.name })),
            ]}
            onChange={(next) => {
              setConnectionId(next);
            }}
          />
          <SelectField
            label={t('scheduling.form.strategy')}
            value={strategy}
            options={[
              { value: '', label: t('scheduling.form.strategy.choose') },
              ...SCHEDULABLE_STRATEGIES.map((each) => ({
                value: each,
                label: t(`scheduling.strategy.${each}`),
              })),
            ]}
            onChange={(next) => {
              setStrategy(next);
            }}
          />
          <SelectField
            label={t('scheduling.form.severityFloor')}
            value={floor}
            options={SEVERITY_FLOORS.map((each) => ({
              value: each,
              label: t(`scheduling.severity.${each}`),
            }))}
            onChange={(next) => {
              setFloor(next);
            }}
          />
        </div>
        {/* **No barrier over several connections, and the reason is on the screen.** "After all
            three harvests" needs a definition of "the last one" and a timeout for a harvest that
            never runs — and without the timeout one dead schedule holds an execution for ever. A
            reader who wants it is told what to do instead rather than left to look for a control
            that is not there. */}
        <p className="lq-sched__inline lq-sched--info">
          {t('scheduling.trigger.noBarrier')}
        </p>
        {problem !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{t(problem)}</p>
        ) : null}
        {refusal !== null ? (
          <p className="lq-sched__inline lq-sched--danger">{refusal}</p>
        ) : null}
        <div className="lq-sched__actions">
          <Button
            tone="primary"
            disabled={problem !== null}
            busy={configure.isPending}
            onClick={() => {
              if (strategy === '' || connectionId === '') {
                return;
              }
              configure.mutate(
                {
                  bindingId,
                  body: {
                    enabled: stored.configured ? stored.enabled : false,
                    connection_id: connectionId,
                    strategy,
                    severity_floor: floor,
                  },
                },
                {
                  onSuccess: () => {
                    setEditing(false);
                  },
                },
              );
            }}
          >
            {t('scheduling.form.save')}
          </Button>
          <Button
            tone="secondary"
            onClick={() => {
              setEditing(false);
              configure.reset();
            }}
          >
            {t('scheduling.form.cancel')}
          </Button>
        </div>
      </Card>
    );
  }

  if (presence.kind === 'unconfigured') {
    return (
      <Card title={title}>
        <p className="lq-sched__line">
          <span className="lq-sched__lead">{t('scheduling.state.unconfigured')}</span>{' '}
          {t('scheduling.trigger.unconfigured')}
        </p>
        <div className="lq-sched__actions">
          <Button tone="secondary" onClick={openEditor}>
            {t('scheduling.trigger.setTrigger')}
          </Button>
        </div>
      </Card>
    );
  }

  /* **Nothing has reached this trigger, and that is a finding rather than a blank.** A null
     `last_considered_at` on a connection that harvests nightly means no completion is arriving —
     worth looking at, which is exactly the absence the design asks to be named rather than left as
     an empty cell. */
  const neverReached =
    stored.last_considered_at === null || stored.last_considered_at === undefined;

  return (
    <Card title={title} hint={<StateChip presence={presence} />}>
      <div className={presence.kind === 'disabled' ? 'lq-sched__dimmed' : undefined}>
        <p className="lq-sched__describes">{stored.describes ?? ''}</p>
        {/* The connection by **name**, resolved here: the API returns only an id, and a uuid inside
            the sentence would make the sentence unreadable while telling a reader nothing they
            could not look up. The id is the title, for whoever is debugging one row. */}
        <p className="lq-sched__detail" title={stored.connection_id ?? ''}>
          {t('scheduling.trigger.connection', {
            name: sourceName ?? t('scheduling.trigger.connectionUnresolved'),
          })}
        </p>
        <div className="lq-sched__facts">
          {/* **Two instants, and they are not the same fact.** `last_considered_at` says a harvest
              reached this trigger at all; `last_executed_at` says it last actually caused a scan,
              and is what the cooldown is measured from. Collapsing them into one "last run" would
              hide the case the split exists for: a failed harvest that was considered and skipped
              without starting the cooldown, so the retry that fixes it still fires. */}
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.lastConsidered')}</div>
            <div className="lq-sched__value lq-mono">
              {formatInstant(stored.last_considered_at) ??
                stored.last_considered_at ??
                t('scheduling.trigger.neverReached.short')}
            </div>
          </div>
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.lastExecuted')}</div>
            <div className="lq-sched__value lq-mono">
              {formatInstant(stored.last_executed_at) ?? stored.last_executed_at ?? '—'}
            </div>
          </div>
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.coolsUntil')}</div>
            <div className="lq-sched__value lq-mono">
              {formatInstant(stored.cools_until) ?? stored.cools_until ?? '—'}
            </div>
          </div>
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.causedBy')}</div>
            <div className="lq-sched__value lq-mono">{stored.last_harvest_id ?? '—'}</div>
          </div>
          <div>
            <div className="lq-sched__label">{t('scheduling.fact.strategy')}</div>
            <div className="lq-sched__value">
              {stored.strategy === undefined
                ? '—'
                : t(`scheduling.strategy.${stored.strategy}.long`)}
            </div>
          </div>
          {outcome !== null ? (
            <div>
              <div className="lq-sched__label">{t('scheduling.fact.outcome')}</div>
              <div className="lq-sched__value">
                <span className={`lq-sched__outcome lq-sched--${OUTCOME_TONE[outcome]}`}>
                  {t(`scheduling.outcome.${outcome}`)}
                </span>
              </div>
            </div>
          ) : null}
        </div>
        {stored.last_detail !== null && stored.last_detail !== undefined ? (
          <p className="lq-sched__detail">{stored.last_detail}</p>
        ) : null}
      </div>

      {neverReached && presence.kind !== 'disabled' ? (
        <p className="lq-sched__inline lq-sched--info">
          {t('scheduling.trigger.neverReached', {
            name: sourceName ?? t('scheduling.trigger.connectionUnresolved'),
          })}
        </p>
      ) : null}

      <StateNotes presence={presence} needsAttention={stored.needs_attention === true} />

      {/* **A deferral waits for the next harvest, not a next occurrence**, and the design says so
          because a reader would otherwise expect a retry that is not coming. */}
      {outcome === 'deferred' ? (
        <p className="lq-sched__note">{t('scheduling.trigger.deferredNote')}</p>
      ) : null}

      <p className="lq-sched__note">{t('scheduling.trigger.noClock')}</p>

      {!durable ? (
        <p className="lq-sched__inline lq-sched--warn">
          {t('scheduling.state.notDurable')}
        </p>
      ) : null}

      <ToggleRow
        enabled={stored.enabled}
        busy={setEnabled.isPending}
        error={
          setEnabled.isError
            ? setEnabled.error
            : configure.error instanceof ApiError && configure.error.status === 403
              ? configure.error
              : null
        }
        onEdit={openEditor}
        onToggle={() => {
          setEnabled.mutate({ id: bindingId, enabled: !stored.enabled });
        }}
      />
    </Card>
  );
}

/* ------------------------------------------------------------------ shared pieces */

function refusalOf(
  error: Error | null,
  failed: boolean,
  t: (key: string) => string,
): string | null {
  if (error instanceof ApiError) {
    return error.message;
  }
  return failed ? t('scheduling.form.saveFailed') : null;
}

/** A 403 keeps the facts and says what grant is missing; anything else is a failed read.
 *
 * The two are separated because they send a reader to different people: one asks an administrator
 * for `rules.execution_schedule:configure`, the other is a request to retry. Reporting a permission
 * answer as a broken fetch is the conflation §9 records the transport layer being written to avoid.
 */
function RefusedCard({
  title,
  error,
  kind,
}: {
  readonly title: string;
  readonly error: Error | null;
  readonly kind: 'execution' | 'trigger';
}): ReactElement {
  const { t } = useTranslation();
  const forbidden = error instanceof ApiError && error.status === 403;
  return (
    <Card title={title}>
      <p className="lq-sched__detail">
        {forbidden ? t('scheduling.forbidden') : t(`scheduling.${kind}.readFailed`)}
      </p>
    </Card>
  );
}

function StateNotes({
  presence,
  needsAttention,
}: {
  readonly presence: ReturnType<typeof presenceOf>;
  readonly needsAttention: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  return (
    <>
      {presence.kind === 'disabled' ? (
        <p className="lq-sched__note">{t('scheduling.state.disabled.note')}</p>
      ) : null}
      {presence.kind === 'suspended' ? (
        <>
          <p className="lq-sched__inline lq-sched--warn">
            {t('scheduling.state.suspended.reason', { reason: presence.reason })}
          </p>
          <p className="lq-sched__note">{t('scheduling.state.suspended.note')}</p>
        </>
      ) : null}
      {needsAttention ? (
        <p className="lq-sched__inline lq-sched--danger">
          {t('scheduling.state.needsAttention')}
        </p>
      ) : null}
    </>
  );
}

/** The action row, or the read-only sentence once a write has been refused.
 *
 * **Shared by both binding cards, so neither can forget the read-only state.** A 403 here refuses
 * the *write*: `get` authorizes on `read` and `configure` on `configure`, so a reader holding the
 * first and not the second sees every fact and cannot change one. There is no grant list on the
 * wire for a client to check in advance, so the sentence appears once a write comes back refused —
 * and the controls go with it, because a button known to be refused invites a second press.
 */
function ToggleRow({
  enabled,
  busy,
  error,
  onEdit,
  onToggle,
}: {
  readonly enabled: boolean;
  readonly busy: boolean;
  readonly error: Error | null;
  readonly onEdit: () => void;
  readonly onToggle: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const forbidden = error instanceof ApiError && error.status === 403;

  if (forbidden) {
    return <p className="lq-sched__note">{t('scheduling.forbidden')}</p>;
  }
  return (
    <>
      {error !== null ? (
        <p className="lq-sched__inline lq-sched--danger">
          {error instanceof ApiError ? error.message : t('scheduling.form.toggleFailed')}
        </p>
      ) : null}
      <div className="lq-sched__actions">
        <Button tone="secondary" onClick={onEdit}>
          {t('scheduling.form.edit')}
        </Button>
        <Button tone="secondary" busy={busy} onClick={onToggle}>
          {enabled ? t('scheduling.form.disable') : t('scheduling.form.enable')}
        </Button>
      </div>
    </>
  );
}
